import { useEffect, useState } from 'react';
import {
  AppBar,
  Autocomplete,
  Avatar,
  Badge,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Grid,
  LinearProgress,
  Link,
  Pagination,
  TextField,
  Typography,
  styled,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { PureLightTheme } from 'src/theme/schemes/PureLightTheme';
import { CircularProgressForObject, GridContainer, GridItem } from './Index';
import {
  AddTwoTone,
  Circle,
  HomeWorkOutlined,
  MenuBook,
  RemoveRedEyeOutlined,
  SchoolOutlined
} from '@mui/icons-material';
import DefaultAvatar from 'src/assets/icons/DefaultAvatar.png';
import RemoveOutlinedIcon from '@mui/icons-material/RemoveOutlined';
import EmptyBoxIcon from 'src/assets/icons/empty-box.png';
import EmptyStudents from 'src/assets/icons/empty-students.png';
import { Dropdown } from 'src/models';
import ActivityDefaultImage from 'src/assets/images/activity-common.jpg';
import SubjectImage from 'src/assets/images/subject_common.jpg';
import { filePaths } from 'src/utils/properties';
import 'src/css/style.css';

const EmptyStudentsIcon = (props) => {
  return <img {...props} src={EmptyStudents} style={{ width: '100px' }} />;
};

const EmptyStudentText = styled(Typography)(({ theme }) => ({
  fontFamily: 'Poppins',
  fontWeight: 700,
  fontSize: '16px',
  color: '#FA7B17',
  textAlign: 'center'
}));

const ParentDashboardBox = styled(Box)(() => ({
  padding: '20px'
}));

const PageCoverTitle = styled(Typography)(({ theme }) => ({
  color: theme.colors.alpha.white[100],
  fontFamily: 'Poppins',
  fontWeight: 600,
  [theme.breakpoints.up('xs')]: {
    fontSize: '18px'
  },
  [theme.breakpoints.up('sm')]: {
    fontSize: '25px'
  },
  [theme.breakpoints.up('md')]: {
    fontSize: '32px'
  },
  [theme.breakpoints.up('lg')]: {
    fontSize: '32px'
  },
  [theme.breakpoints.up('xl')]: {
    fontSize: '40px'
  }
}));

const AddStudentButton = styled(Button)(({ theme }) => ({
  border: '2px #FA7B17 dotted',
  margin: '16px 32px 15px 24px'
}));

const AddStudentIconStyle = styled('img')(() => ({
  alt: 'Add Student',
  width: '66px',
  height: '77px'
}));

const ParentDashboardTabContainer = styled(Box)(({ theme }) => ({
  [theme.breakpoints.up('xs')]: {
    width: '100%'
  }
}));

const ParentDashboardTabAppBar = styled(AppBar)(() => ({
  position: 'static',
  background: '#F2F2F2',
  borderRadius: '8px'
}));

const StudentListBox = (props) => {
  return <Box {...props} className="student-list-box" />;
};

const StudentCardsGridItem = (props) => {
  return <Grid {...props} item xl={3} lg={4} md={6} sm={6} xs={12} />;
};

const StudentAvatar = (props) => {
  return (
    <Avatar
      {...props}
      variant="circular"
      sx={{
        strokeWidth: '2px',
        stroke: '#FFF',
        '.MuiAvatar-fallback': {
          background: `url(${DefaultAvatar}) center no-repeat`,
          backgroundSize: '100% 100%',
          width: '120%',
          height: '120%',
          path: {
            display: 'none'
          }
        },
        [PureLightTheme.breakpoints.up('md')]: {
          width: '40px',
          height: '40px'
        },
        [PureLightTheme.breakpoints.up('lg')]: {
          width: '60px',
          height: '60px'
        }
      }}
    />
  );
};

const ViewProfileButton = styled(Button)(({ theme }) => ({
  fontFamily: 'Inter',
  fontWeight: 400,
  color: theme.colors.alpha.orange[100],
  height: '30px',
  backgroundColor:
    'linear-gradient(180deg, rgba(255, 91, 0, 0.20) 0%, rgba(212, 76, 0, 0.20) 100%), #FA7B17',
  borderRadius: '20px'
}));

const EditProfileButton = styled(Button)(() => ({
  fontFamily: 'Inter',
  fontWeight: 400,
  color: '#343434',
  height: '40px',
  backgroundColor: 'rgba(250, 123, 23, 0.20)',
  borderRadius: '8px'
}));

const ProfileAvatar = styled(Avatar)(() => ({
  width: '75px',
  maxWidth: '75px',
  minWidth: '50px',
  height: 'auto'
}));

const CameraAvatar = styled(Avatar)(({ theme }) => ({
  border: `2px solid ${theme.palette.background.paper}`,
  width: '30px',
  height: '30px',
  cursor: 'pointer'
}));

const DeleteAvatar = styled(Avatar)(({ theme }) => ({
  border: `2px solid ${theme.palette.background.paper}`,
  width: '30px',
  height: '30px',
  cursor: 'pointer'
}));

const EditProfileAvatar = styled(Avatar)(({ theme }) => ({
  '.MuiAvatar-fallback': {
    background: `url(${DefaultAvatar}) center no-repeat`,
    backgroundSize: '100% 100%',
    width: '120%',
    height: '120%',
    path: {
      display: 'none'
    }
  },
  [theme.breakpoints.up('xs')]: {
    width: '120px',
    height: '120px'
  },
  [theme.breakpoints.up('md')]: {
    width: '120px',
    height: '120px'
  }
}));

const NameGrid = styled(Grid)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  [theme.breakpoints.up('xs')]: {
    textAlign: 'center'
  },
  [theme.breakpoints.up('sm')]: {
    textAlign: 'left'
  }
}));

const CreateParentTitle = styled(Grid)(({ theme }) => ({
  fontFamily: 'Poppins',
  fontWeight: '600',
  color: '#343434',
  textAlign: 'left',
  [theme.breakpoints.up('xs')]: {
    fontSize: '20px'
  },
  [theme.breakpoints.up('md')]: {
    fontSize: '32px'
  },
  [theme.breakpoints.up('lg')]: {
    fontSize: '32px'
  },
  [theme.breakpoints.up('xl')]: {
    fontSize: '32px'
  }
}));

const StudentLoginTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 900,
  fontFamily: 'Biryani',
  [theme.breakpoints.up('xs')]: {
    fontSize: '25px'
  },
  [theme.breakpoints.up('md')]: {
    fontSize: '30px'
  },
  [theme.breakpoints.up('lg')]: {
    fontSize: '40px'
  },
  [theme.breakpoints.up('xl')]: {
    fontSize: '50px'
  }
}));

const LinkComponent = styled(
  (props: {
    href: any;
    variant?: any;
    underline?: any;
    color?: any;
    sx?: any;
    fontWeight?: any;
    fontSize?: any;
  }) => (
    <Link
      variant="body2"
      underline="always"
      color="#343434"
      fontFamily="Poppins"
      fontWeight={600}
      sx={{
        [PureLightTheme.breakpoints.up('md')]: {
          fontSize: '15px'
        },
        [PureLightTheme.breakpoints.up('lg')]: {
          fontSize: '16px'
        }
      }}
      {...props}
    />
  )
)({});

const FilterGridContainer = (props) => {
  const theme = useTheme();
  return (
    <GridContainer
      {...props}
      columnSpacing={0.5}
      rowSpacing={1}
      sx={{
        [theme.breakpoints.down('sm')]: {
          background: '#FA7B171A',
          borderRadius: '4px',
          padding: '0 4px 15px 4px',
          marginTop: 1,
          margin: 'auto'
        }
      }}
    />
  );
};

const FilterLabelGridItem = styled(Grid)(() => ({
  item: 'true',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center'
}));

const FilterLabel = styled(Typography)(() => ({
  color: '#9C9C9C',
  fontSize: '12px',
  fontFamily: 'Poppins',
  fontWeight: 400,
  lineHeight: '12px',
  wordWrap: 'break-word',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  marginRight: '6px'
}));

const StyledAutoComplete = (props) => {
  return (
    <Autocomplete
      {...props}
      isOptionEqualToValue={(option: Dropdown, value: Dropdown | null) =>
        option.key === value?.key
      }
      disablePortal
      disableClearable
      size="small"
      fullWidth
      renderInput={(params) => <StyledAutoCompleteTextField {...params} />}
    />
  );
};

const StyledAutoCompleteTextField = (props) => {
  const theme = useTheme();
  const isBelowSmallScreen = useMediaQuery(
    `(min-width: ${theme.breakpoints.values['xs']}px) and (max-width: 400px)`
  );
  return (
    <TextField
      {...props}
      sx={{
        width: '150px',
        justifyContent: 'center',
        fontFamily: 'Poppins',
        '& .MuiOutlinedInput-root': {
          height: '30px',
          '& > fieldset': {
            border: `1px solid ${theme.colors.alpha.orange[100]}`,
            borderRadius: '20px'
          }
        },
        '& .MuiInputBase-input': {
          color: theme.colors.alpha.orange[100]
        },
        '& .MuiSvgIcon-root': {
          color: theme.colors.alpha.orange[100],
          border: `1px solid ${theme.colors.alpha.orange[100]}`,
          borderRadius: '15px',
          fontSize: '18px'
        },
        [theme.breakpoints.down('sm')]: {
          width: isBelowSmallScreen ? '86px' : '110px'
        },
        [theme.breakpoints.up('sm')]: {
          fontSize: '14px'
        },
        [theme.breakpoints.up('md')]: {
          fontSize: '16px'
        }
      }}
    />
  );
};

const StyledDatePicker = styled(TextField)(({ theme }) => ({
  width: '140px',
  justifyContent: 'center',
  fontFamily: 'Poppins',
  '& .MuiOutlinedInput-root': {
    height: '30px',
    '& > fieldset': {
      border: `1px solid ${theme.colors.alpha.orange[100]}`,
      borderRadius: '25px'
    },
    '&::placeholder': {
      color: theme.colors.alpha.orange[100]
    }
  },
  '& .MuiInputBase-input': {
    color: theme.colors.alpha.orange[100]
  },
  '& .MuiSvgIcon-root': {
    color: theme.colors.alpha.orange[100]
  },
  [theme.breakpoints.down('sm')]: { width: '100%', marginTop: '10px' },
  [theme.breakpoints.up('sm')]: {
    fontSize: '14px'
  },
  [theme.breakpoints.up('md')]: {
    fontSize: '16px'
  }
}));

const ShowRecordsButton = styled(Button)(({ theme }) => ({
  height: '30px',
  borderRadius: '20px',
  color: theme.colors.alpha.trueWhite[100],
  fontSize: '12px',
  background: theme.colors.alpha.orange[100],
  [theme.breakpoints.down('sm')]: { width: '100%', marginTop: '10px' },
  [theme.breakpoints.up('sm')]: {
    maxWidth: '170px'
  },
  '&:hover': {
    background: theme.colors.alpha.orange[70]
  }
}));

const ImageContainer = styled(Box)(() => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center'
}));

const ImageCard = styled((props: { src: any; alt: any; sx?: any }) => {
  const theme = useTheme();
  return (
    <Card
      sx={{
        background: '#2d3436',
        [theme.breakpoints.up('xs')]: {},
        [theme.breakpoints.up('sm')]: {},
        [theme.breakpoints.up('md')]: {},
        [theme.breakpoints.up('lg')]: {
          width: '380px',
          height: '350px'
        },
        [theme.breakpoints.up('xl')]: {
          width: '166px',
          height: '185px',
          borderRadius: '4px'
        }
      }}
      {...props}
    >
      <img
        src={props.src}
        alt={props.alt}
        style={{
          height: '100%',
          width: '100%',
          objectFit: 'contain'
        }}
      />
    </Card>
  );
})({});

const StudentRecordLinearProgressComponent = (props) => {
  return (
    <LinearProgress
      {...props}
      variant="determinate"
      classes={{
        root: 'linear-root',
        bar: 'linear-bar'
      }}
    />
  );
};

const ParentDashboardCoverImage = (props) => {
  return <img {...props} className="parent-dashboard-img" />;
};

const MyStudentsStudentNameText = (props) => {
  return <Typography {...props} className="student-name-text" />;
};

const MyStudentsStudentEmailText = (props) => {
  return <Typography {...props} className="student-email-text" />;
};

const StatusStyledChip = (props) => {
  return (
    <Chip
      {...props}
      variant="outlined"
      className="status-chip"
      sx={{ border: '1px solid rgba(250, 123, 23, 0.20)', height: '25px' }}
    />
  );
};

const StyledCircleIcon = styled(Circle)(() => ({
  fontSize: '20px'
}));

const MyStudentsInfoLabel = (props) => {
  return <Typography {...props} className="info-label" />;
};

const MyStudentsCardViewProfileButton = (props) => {
  return <Button {...props} variant="outlined" className="view-profile-btn" />;
};

const MyStudentsCardViewProfileButtonGridItem = (props) => {
  return <GridItem {...props} xs={6} margin="auto" />;
};

const MyStudentsCardEditProfileButton = (props) => {
  return <Button {...props} variant="contained" className="edit-profile-btn" />;
};

const MyStudentsInfoText = (props) => {
  return <Typography {...props} className="info-text" />;
};

const EditProfileFileInput = (props) => {
  return (
    <input
      {...props}
      type="file"
      accept="image/png, image/jpeg, image/jpg"
      style={{ display: 'none' }}
    />
  );
};

const EditProfileCameraBadge = (props) => {
  return (
    <Badge
      {...props}
      overlap="circular"
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right'
      }}
    />
  );
};

const EditProfileDeleteBadge = (props) => {
  return (
    <Badge
      {...props}
      overlap="circular"
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left'
      }}
    />
  );
};

const EnrolledSubjectCard = (props) => {
  return <Card {...props} className="enrolled-subject-card" />;
};

const EnrolledSubjectCardGridContainer = (props) => {
  return <GridContainer {...props} margin="12px 0 0 10px" />;
};

const NothingEnrolledSubjectCard = (props) => {
  const theme = useTheme();
  return (
    <Card
      {...props}
      sx={{
        background: '#FA7B170F',
        borderRadius: '8px',
        padding: '40px 35px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        boxShadow: 'none',
        border: `1px solid ${theme.colors.alpha.orange[100]}`
      }}
    />
  );
};

const UpdateProfileTitleGrid = styled(Grid)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  marginLeft: 15
}));

const EmptyIcon = (props) => {
  return <img {...props} src={EmptyBoxIcon} style={{ width: '100px' }} />;
};

const NothingToShowGrid = styled(Grid)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center'
}));

const NothingToShowText = styled(Typography)(({ theme }) => ({
  fontSize: '20px',
  fontWeight: 400,
  fontFamily: 'Poppins',
  textAlign: 'justify',
  color: theme.colors.alpha.orange[100],
  [theme.breakpoints.up('xs')]: { fontSize: '16px' }
}));

const EnrolledSubjectCardInfoBodyGridItem = (props) => {
  return (
    <GridItem {...props} item xl={9.6} lg={8.5} md={7} sm={8.5} xs={7.6} />
  );
};

const EnrolledSubjectCardInfoBodyGridContainer = (props) => {
  return <GridContainer {...props} />;
};

const SubjectNameText = (props) => {
  return <Typography {...props} className="subject-name" />;
};

const SubjectDescriptionText = (props) => {
  return <Typography {...props} className="subject-description" />;
};

const EnrolledSubjectCardAvatarAndInfoGridContainer = (props) => {
  return (
    <GridContainer {...props} justifyContent="flex-end" columnSpacing={1} />
  );
};

const ActivityProgressTextGrid = (props) => {
  return <Grid {...props} className="activities-progress-text-grid-item" />;
};

const ActivityProgressText = (props) => {
  return <Typography {...props} className="activities-progress-text" />;
};

const EnrolledSubjectProfileAvatar = (props) => {
  return (
    <Avatar
      {...props}
      variant="circular"
      sx={{
        strokeWidth: '2px',
        stroke: '#FFF',
        '.MuiAvatar-fallback': {
          background: `url(${DefaultAvatar}) center no-repeat`,
          backgroundSize: '100% 100%',
          width: '120%',
          height: '120%',
          path: {
            display: 'none'
          }
        },
        [PureLightTheme.breakpoints.down('md')]: {
          height: '24px',
          width: '24px'
        },
        [PureLightTheme.breakpoints.up('md')]: {
          height: '32px',
          width: '32px'
        }
      }}
    />
  );
};

const EnrolledSubjectViewCard = (props) => {
  const theme = useTheme();
  return (
    <Card
      {...props}
      sx={{
        border: '1px solid #fa7b17',
        borderRadius: '8px',
        [theme.breakpoints.up('xs')]: { height: '133px' },
        [theme.breakpoints.up('sm')]: { height: '211px' },
        [theme.breakpoints.up('md')]: {},
        [theme.breakpoints.up('lg')]: {},
        [theme.breakpoints.up('xl')]: {}
      }}
    />
  );
};

const EnrolledSubjectViewCardGridContainer = (props) => {
  return <GridContainer {...props} p="12px 10px 0 12px" columnSpacing={4} />;
};

const ESImageCard = styled((props: any) => {
  const theme = useTheme();
  return (
    <Card
      sx={{
        background: '#2d3436',
        borderRadius: '4px',
        [theme.breakpoints.up('xs')]: { width: '96px', height: '107px' },
        [theme.breakpoints.up('sm')]: { width: '166px', height: '185px' },
        [theme.breakpoints.up('md')]: { width: '166px', height: '185px' },
        [theme.breakpoints.up('lg')]: {},
        [theme.breakpoints.up('xl')]: {}
      }}
      {...props}
    >
      <img
        src={props.src}
        alt={props.alt}
        style={{
          height: '100%',
          width: '100%',
          objectFit: 'contain'
        }}
      />
    </Card>
  );
})({});

const ESVImageCardGridItem = (props) => {
  return <Grid {...props} item xl={1.2} lg={1.7} md={2.3} sm={3} xs={3.7} />;
};

const ESImageCardGridItem = (props) => {
  return <Grid {...props} item xl={2.4} lg={3.5} md={5} sm={3.5} xs={4.4} />;
};

const ESVInfoBodyGridItem = (props) => {
  return <Grid item xl={10.8} lg={10.3} md={9.7} sm={9} xs={8.3} />;
};

const ESVInfoBodyGridContainer = (props) => {
  return <GridContainer {...props} justifyContent="flex-end" />;
};

const ESVCardAvatarAndInfoGridContainer = (props) => {
  return (
    <GridContainer {...props} justifyContent="flex-end" columnSpacing={1} />
  );
};

const ESVActivityProgressGridContainer = (props) => {
  const theme = useTheme();
  return (
    <GridContainer
      {...props}
      sx={{
        [theme.breakpoints.up('xs')]: { marginTop: '2px' },
        [theme.breakpoints.up('sm')]: { marginTop: '10px', rowGap: '5px' },
        [theme.breakpoints.up('md')]: { marginTop: '30px' },
        [theme.breakpoints.up('lg')]: { marginTop: '50px' },
        [theme.breakpoints.up('xl')]: { marginTop: '60px' }
      }}
    />
  );
};

const ESActivityProgressGridContainer = (props) => {
  const theme = useTheme();
  return (
    <GridContainer
      {...props}
      rowSpacing={1}
      sx={{
        [theme.breakpoints.up('xs')]: {},
        [theme.breakpoints.up('sm')]: { marginTop: '10px', rowGap: '5px' },
        [theme.breakpoints.up('md')]: { marginTop: '30px' },
        [theme.breakpoints.up('lg')]: { marginTop: '50px' },
        [theme.breakpoints.up('xl')]: { marginTop: '60px' }
      }}
    />
  );
};

const CurrentActivityCard = styled(Card)(({ theme }) => ({
  background: '#F5F8FF',
  padding: '20px',
  width: '100%',
  borderRadius: '12px',
  [theme.breakpoints.up('xs')]: {},
  [theme.breakpoints.up('sm')]: {},
  [theme.breakpoints.up('md')]: { height: '350px' }
}));

const StudentAttendanceCard = styled(Card)(({ theme }) => ({
  background: '#F5F8FF',
  padding: '20px',
  width: '100%',
  borderRadius: '12px',
  height: '350px'
}));

const CurrentActivityCode = styled(Typography)(({ theme }) => ({
  color: '#FA7B17',
  fontWeight: 500,
  fontSize: '16px',
  [theme.breakpoints.down('sm')]: { textAlign: 'left' },
  [theme.breakpoints.up('sm')]: { textAlign: 'right' },
  [theme.breakpoints.up('md')]: {},
  [theme.breakpoints.up('lg')]: {}
}));

const CountCard = styled(Card)(({ theme }) => ({
  padding: '10px',
  width: '100%',
  height: '40px',
  borderRadius: '2px'
}));

const MenuBookIcon = styled(MenuBook)(({ theme }) => ({
  color: theme.colors.alpha.orange[100]
}));

const SchoolOutlinedIcon = styled(SchoolOutlined)(({ theme }) => ({
  color: theme.colors.alpha.orange[100]
}));

const HomeWorkOutlinedIcon = styled(HomeWorkOutlined)(({ theme }) => ({
  color: theme.colors.alpha.orange[100]
}));

const CountText = styled(Typography)(() => ({
  fontFamily: 'Poppins',
  fontSize: '12px',
  color: '#343434',
  fontWeight: 700,
  textAlign: 'right'
}));

const RRTitleHeaderBox = styled(Box)(({ theme }) => ({
  background: '#FA7B1733',
  width: '100%',
  borderRadius: '4px',
  padding: '10px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  [theme.breakpoints.up('xs')]: {
    height: '24px'
  },
  [theme.breakpoints.up('sm')]: {
    height: '60px'
  }
}));

const RRResultsBox = styled(Box)(({ theme }) => ({
  background: 'linear-gradient(90deg, #FA7B17 0%, #FFB038 100%)',
  width: '100%',
  borderRadius: '2px',
  paddingLeft: '10px',
  [theme.breakpoints.up('xs')]: {
    height: '30px'
  },
  [theme.breakpoints.up('sm')]: {
    height: '60px'
  }
}));

const FlexGrid = styled(Grid)(() => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center'
}));

const ViewMoreButton = (props) => {
  const theme = useTheme();
  return (
    <Box
      {...props}
      sx={{
        background: '#FA7B17',
        textAlign: 'center',
        [theme.breakpoints.up('xs')]: {
          width: '24px'
        },
        [theme.breakpoints.up('sm')]: {
          width: '60px',
          padding: '15px 0'
        }
      }}
    >
      <RemoveRedEyeOutlined
        sx={{ color: theme.colors.alpha.trueWhite[100], paddingTop: '6px' }}
      />
    </Box>
  );
};

const ViewLessButton = (props) => {
  const theme = useTheme();
  return (
    <Box
      {...props}
      sx={{
        background: '#FA7B17',
        textAlign: 'center',
        [theme.breakpoints.up('xs')]: {
          width: '24px'
        },
        [theme.breakpoints.up('sm')]: {
          width: '60px',
          padding: '15px 0'
        }
      }}
    >
      <RemoveOutlinedIcon
        sx={{ color: theme.colors.alpha.trueWhite[100], paddingTop: '6px' }}
      />
    </Box>
  );
};

const CollapseContainer = styled(GridContainer)(({ theme }) => ({
  background: '#F5F8FF',
  [theme.breakpoints.up('xs')]: {
    padding: '5px'
  },
  [theme.breakpoints.up('sm')]: {
    padding: '30px'
  }
}));

const CollapseCard = styled(Card)(() => ({
  borderRadius: 0,
  width: '100%',
  padding: '30px',
  position: 'relative'
}));

export const ActivityNameTitle = styled(Typography)(({ theme }) => ({
  color: '#343434',
  fontFamily: 'Poppins',
  fontWeight: 700,
  [theme.breakpoints.up('xs')]: {
    fontSize: '14px'
  },
  [theme.breakpoints.up('sm')]: {
    fontSize: '16px'
  },
  [theme.breakpoints.up('md')]: {
    fontSize: '20px'
  },
  [theme.breakpoints.up('lg')]: {
    fontSize: '20px'
  },
  [theme.breakpoints.up('xl')]: {
    fontSize: '20px'
  }
}));

const ReportText = styled(Typography)(({ theme }) => ({
  fontFamily: 'Poppins',
  color: '#343434',
  fontWeight: 400,
  [theme.breakpoints.up('xs')]: {
    fontSize: '10px'
  },
  [theme.breakpoints.up('sm')]: {
    fontSize: '16px'
  },
  [theme.breakpoints.up('md')]: {
    fontSize: '16px'
  },
  [theme.breakpoints.up('lg')]: {
    fontSize: '16px'
  },
  [theme.breakpoints.up('xl')]: {
    fontSize: '16px'
  }
}));

const ReportNumberTitle = styled(Typography)(({ theme }) => ({
  fontFamily: 'Poppins',
  color: '#343434',
  fontWeight: 400,
  textAlign: 'right',
  [theme.breakpoints.up('xs')]: {
    fontSize: '10px'
  },
  [theme.breakpoints.up('sm')]: {
    fontSize: '16px'
  },
  [theme.breakpoints.up('md')]: {
    fontSize: '16px'
  },
  [theme.breakpoints.up('lg')]: {
    fontSize: '16px'
  },
  [theme.breakpoints.up('xl')]: {
    fontSize: '16px'
  }
}));

const QuestionTitleGrid = styled(Grid)(({ theme }) => ({
  [theme.breakpoints.up('xs')]: { marginLeft: '10px' },
  [theme.breakpoints.up('sm')]: { marginLeft: '40px' }
}));

const YourAnswerGridContainer = (props) => {
  const theme = useTheme();
  return (
    <GridContainer
      {...props}
      rowSpacing="10px"
      sx={{
        background: 'rgba(255, 239, 143, 0.10)',
        [theme.breakpoints.up('xs')]: { padding: '10px 10px 10px 10px' },
        [theme.breakpoints.up('sm')]: { padding: '20px 20px 20px 40px' },
        [theme.breakpoints.up('lg')]: {},
        [theme.breakpoints.up('xl')]: {}
      }}
    />
  );
};

const CorrectAnswerGridContainer = styled(GridContainer)(({ theme }) => ({
  background: '#F9FFFE',
  padding: '20px 20px 20px 40px',
  [theme.breakpoints.up('md')]: {},
  [theme.breakpoints.up('lg')]: {},
  [theme.breakpoints.up('xl')]: {}
}));

const ResultsContainerGridItem = styled(GridItem)(({ theme }) => ({
  marginBottom: '40px'
}));

const ResultsPagination = (props) => {
  const theme = useTheme();
  return (
    <Pagination
      {...props}
      variant="outlined"
      sx={{
        position: 'absolute',
        bottom: 20,
        [theme.breakpoints.up('xs')]: {
          transform: 'translateX(10%)'
        },
        [theme.breakpoints.up('sm')]: {
          left: '50%',
          transform: 'translateX(-50%)'
        }
      }}
    />
  );
};

const ChartMonthText = styled(Typography)(({ theme }) => ({
  fontFamily: 'Poppins',
  fontWeight: 600,
  fontSize: '16px'
}));

const ChartDateText = styled(Typography)(({ theme }) => ({
  fontFamily: 'Poppins',
  fontWeight: 400,
  fontSize: '12px',
  color: '#9C9C9C'
}));

const DateContainer = styled(GridContainer)(({ theme }) => ({
  height: '100%'
}));

const CircleGridItem = styled(Grid)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center'
}));

const CircleIcon = (props) => {
  return <Circle {...props} sx={{ color: props.color, fontSize: '15px' }} />;
};

const StudentListCardContainer = styled(Card)(({ theme }) => ({
  minHeight: '600px',
  borderRadius: '8px',
  [PureLightTheme.breakpoints.up('xs')]: {
    boxShadow: 'none',
    padding: '10px'
  },
  [PureLightTheme.breakpoints.up('sm')]: {
    padding: '25px'
  },
  [PureLightTheme.breakpoints.up('lg')]: {
    padding: '30px'
  },
  width: '100%'
}));

const SuspenseLoaderContainer = styled(Card)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center'
}));

const CenterGridItem = (props) => {
  return (
    <Grid
      {...props}
      item
      xs={12}
      md={12}
      sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
    />
  );
};

const EmptyStudentsBox = styled(Box)(({ theme }) => ({
  width: '30%'
}));

const NoStudentsAddStudentButton = (props) => {
  const theme = useTheme();
  return (
    <Button
      {...props}
      startIcon={<AddTwoTone fontSize="small" />}
      sx={{
        borderRadius: '24px',
        color: theme.colors.alpha.trueWhite[100],
        background: theme.colors.alpha.orange[100],
        marginLeft: '10px',
        [theme.breakpoints.up('xs')]: {},
        [theme.breakpoints.down('sm')]: { width: '100%' },
        [theme.breakpoints.up('sm')]: {
          minWidth: '200px',
          height: '52px',
          fontSize: '20px'
        },

        '&:hover': {
          background: theme.colors.alpha.orange[70]
        }
      }}
    />
  );
};

const SubjectCoverImageCard = styled((props: { src: any; sx?: any }) => {
  const theme = useTheme();
  const [imgSrc, setImgSrc] = useState(
    props.src === null || props.src === undefined
      ? SubjectImage
      : `${filePaths.SUBJECT + props.src}?t=${new Date().getTime()}`
  );
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    setImgSrc(
      props.src === null || props.src === undefined
        ? SubjectImage
        : `${filePaths.SUBJECT + props.src}?t=${new Date().getTime()}`
    );
  }, [props.src]);

  const handleLoad = () => {
    setLoading(false);
  };

  const handleError = () => {
    setImgSrc(SubjectImage);
    setLoading(false);
  };

  return (
    <Box
      sx={{
        borderRadius: '4px',
        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.2)',
        position: 'relative',
        overflow: 'hidden',
        [theme.breakpoints.up('xs')]: { width: '96px', height: '107px' },
        [theme.breakpoints.up('sm')]: { width: '166px', height: '185px' },
        [theme.breakpoints.up('md')]: { width: '166px', height: '185px' },
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `url(${imgSrc})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          filter: 'blur(20px)',
          zIndex: 0
        }
      }}
      {...props}
    >
      {loading && <CircularProgressForObject />}
      <img
        src={imgSrc}
        onLoad={handleLoad}
        onError={handleError}
        style={{
          height: '100%',
          width: '100%',
          objectFit: 'contain',
          zIndex: 1,
          position: 'relative',
          display: loading ? 'none' : 'block' // Hide image while loading
        }}
      />
    </Box>
  );
})({});

const ActivityCoverImageCard = styled((props: { src: any; sx?: any }) => {
  const theme = useTheme();

  const [imgSrc, setImgSrc] = useState(
    props.src === null || props.src === undefined
      ? ActivityDefaultImage
      : `${filePaths.ACTIVITY + props.src}?t=${new Date().getTime()}`
  );
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (props.src) {
      setLoading(true);
      setImgSrc(`${filePaths.ACTIVITY + props.src}?t=${new Date().getTime()}`);
    } else {
      setImgSrc(ActivityDefaultImage);
      setLoading(false);
    }
  }, [props.src]);

  const handleLoad = () => {
    setLoading(false);
  };

  const handleError = () => {
    setImgSrc(ActivityDefaultImage);
    setLoading(false);
  };

  return (
    <Box
      sx={{
        borderRadius: '4px',
        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.2)',
        position: 'relative',
        overflow: 'hidden',
        height: '107px',
        [theme.breakpoints.up('xs')]: { width: '96px' },
        [theme.breakpoints.up('sm')]: { width: '500px' },
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `url(${imgSrc})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          filter: 'blur(8px)',
          zIndex: 0
        }
      }}
      {...props}
    >
      {loading && <CircularProgressForObject />}
      <img
        src={imgSrc}
        onLoad={handleLoad}
        onError={handleError}
        style={{
          height: '100%',
          width: '100%',
          objectFit: 'contain',
          zIndex: 1,
          position: 'relative'
        }}
      />
    </Box>
  );
})({});

const LoaderComponent = () => {
  return (
    <Box sx={{ marginLeft: '50%', marginTop: '10%' }}>
      <CircularProgress size={30} thickness={3} />
    </Box>
  );
};

export {
  EmptyStudentsIcon,
  EmptyStudentText,
  ParentDashboardBox,
  PageCoverTitle,
  AddStudentButton,
  AddStudentIconStyle,
  ParentDashboardTabContainer,
  ParentDashboardTabAppBar,
  StudentListBox,
  StudentAvatar,
  ViewProfileButton,
  EditProfileButton,
  ProfileAvatar,
  CameraAvatar,
  DeleteAvatar,
  EditProfileAvatar,
  NameGrid,
  CreateParentTitle,
  StudentLoginTitle,
  LinkComponent,
  FilterGridContainer,
  FilterLabelGridItem,
  FilterLabel,
  StyledAutoComplete,
  StyledAutoCompleteTextField,
  StyledDatePicker,
  ShowRecordsButton,
  ImageContainer,
  ImageCard,
  StudentRecordLinearProgressComponent,
  ParentDashboardCoverImage,
  MyStudentsStudentNameText,
  MyStudentsStudentEmailText,
  StatusStyledChip,
  StyledCircleIcon,
  MyStudentsInfoLabel,
  MyStudentsCardViewProfileButton,
  MyStudentsCardEditProfileButton,
  MyStudentsInfoText,
  EditProfileFileInput,
  EditProfileCameraBadge,
  EditProfileDeleteBadge,
  EnrolledSubjectCard,
  EnrolledSubjectCardGridContainer,
  EnrolledSubjectCardInfoBodyGridContainer,
  SubjectNameText,
  SubjectDescriptionText,
  EnrolledSubjectCardAvatarAndInfoGridContainer,
  ActivityProgressTextGrid,
  ActivityProgressText,
  EnrolledSubjectProfileAvatar,
  EnrolledSubjectViewCard,
  EnrolledSubjectViewCardGridContainer,
  ESImageCard,
  ESImageCardGridItem,
  ESVImageCardGridItem,
  ESVInfoBodyGridItem,
  ESVInfoBodyGridContainer,
  ESVCardAvatarAndInfoGridContainer,
  ESVActivityProgressGridContainer,
  ESActivityProgressGridContainer,
  CurrentActivityCard,
  CurrentActivityCode,
  CountCard,
  MenuBookIcon,
  SchoolOutlinedIcon,
  HomeWorkOutlinedIcon,
  CountText,
  RRTitleHeaderBox,
  RRResultsBox,
  ViewMoreButton,
  FlexGrid,
  CollapseContainer,
  ViewLessButton,
  CollapseCard,
  ReportText,
  QuestionTitleGrid,
  YourAnswerGridContainer,
  CorrectAnswerGridContainer,
  ResultsPagination,
  StudentAttendanceCard,
  ResultsContainerGridItem,
  ChartMonthText,
  ChartDateText,
  DateContainer,
  CircleGridItem,
  CircleIcon,
  StudentListCardContainer,
  SuspenseLoaderContainer,
  NothingEnrolledSubjectCard,
  EmptyIcon,
  NothingToShowText,
  NothingToShowGrid,
  UpdateProfileTitleGrid,
  StudentCardsGridItem,
  MyStudentsCardViewProfileButtonGridItem,
  EnrolledSubjectCardInfoBodyGridItem,
  CenterGridItem,
  EmptyStudentsBox,
  NoStudentsAddStudentButton,
  ReportNumberTitle,
  SubjectCoverImageCard,
  ActivityCoverImageCard,
  LoaderComponent
};
